import React, { useMemo, useState } from 'react';
import { Card, CardContent, Typography, Box, Button, ButtonGroup, TextField } from '@material-ui/core';
import { ArrowUpward } from '@material-ui/icons';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { SimovsatDistanceByVehicle } from '~/types';
import { format, subDays, startOfDay, endOfDay, isAfter, isBefore, parseISO, startOfMonth, endOfMonth, parse } from 'date-fns';

interface DistanceChartProps {
  distanceByVehicle: SimovsatDistanceByVehicle[];
}

// Cores para diferentes entidades/veículos
const COLORS = [
  '#424242', // Cinza escuro
  '#2196F3', // Azul claro
  '#4CAF50', // Verde escuro
  '#9C27B0', // Roxo brilhante
  '#E0E0E0', // Azul-cinza
  '#F44336', // Vermelho
  '#3F51B5', // Roxo escuro/índigo
];

// Função para gerar cores únicas baseadas em string
const getColorForEntity = (entity: string, index: number): string => {
  return COLORS[index % COLORS.length];
};

// Formatar data para "January 28"
const formatDate = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    return format(date, 'MMMM dd');
  } catch {
    return dateString;
  }
};

type FilterType = 'day' | 'week' | 'fortnight' | 'month' | 'custom';

export default function DistanceChart({ distanceByVehicle }: DistanceChartProps) {
  const [filterType, setFilterType] = useState<FilterType>('week');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [refDate, setRefDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [refMonth, setRefMonth] = useState(format(new Date(), 'yyyy-MM'));

  // Processar dados para formato de barras empilhadas
  const chartData = useMemo(() => {
    // Validar se distanceByVehicle existe e é um array
    if (!distanceByVehicle || !Array.isArray(distanceByVehicle) || distanceByVehicle.length === 0) {
      return [];
    }

    // Agrupar por data
    const groupedByDate: Record<string, Record<string, number>> = {};

    distanceByVehicle.forEach(item => {
      if (!groupedByDate[item.data]) {
        groupedByDate[item.data] = {};
      }
      if (!groupedByDate[item.data][item.entidade]) {
        groupedByDate[item.data][item.entidade] = 0;
      }
      groupedByDate[item.data][item.entidade] += item.registros;
    });

    // Converter para array e ordenar por data
    const dates = Object.keys(groupedByDate).sort((a, b) => {
      return new Date(a).getTime() - new Date(b).getTime();
    });

    // Definir intervalo de datas baseado no filtro
    const now = new Date();
    let startDate: Date | null = null;
    let endDate: Date | null = null;

    const referenceDate = refDate ? parseISO(refDate) : now;

    switch (filterType) {
      case 'day':
        startDate = startOfDay(referenceDate);
        endDate = endOfDay(referenceDate);
        break;
      case 'week':
        startDate = subDays(referenceDate, 6);
        endDate = endOfDay(referenceDate);
        break;
      case 'fortnight':
        startDate = subDays(referenceDate, 14);
        endDate = endOfDay(referenceDate);
        break;
      case 'month':
        if (refMonth) {
          // refMonth is YYYY-MM
          const monthDate = parse(refMonth, 'yyyy-MM', new Date());
          startDate = startOfMonth(monthDate);
          endDate = endOfMonth(monthDate);
        } else {
          startDate = startOfMonth(now);
          endDate = endOfMonth(now);
        }
        break;
      case 'custom':
        if (customStart) startDate = startOfDay(parseISO(customStart));
        if (customEnd) endDate = endOfDay(parseISO(customEnd));
        break;
      default:
        startDate = subDays(now, 7);
        endDate = endOfDay(now);
    }

    // Filtrar datas
    const filteredDates = dates.filter(dateStr => {
      // dateStr é geralmente YYYY-MM-DD
      const date = new Date(dateStr);

      // Se startDate existe, compara.
      if (startDate && isBefore(startOfDay(date), startOfDay(startDate))) return false;
      if (endDate && isAfter(startOfDay(date), endOfDay(endDate))) return false;

      return true;
    });

    // Obter todas as entidades únicas das datas filtradas
    const allEntities = new Set<string>();
    filteredDates.forEach(date => {
      Object.keys(groupedByDate[date]).forEach(entity => allEntities.add(entity));
    });

    const entitiesArray = Array.from(allEntities);

    // Criar dados para o gráfico
    return filteredDates.map(date => {
      const dataPoint: any = {
        data: formatDate(date),
        dataRaw: date,
      };

      entitiesArray.forEach(entity => {
        dataPoint[entity] = groupedByDate[date][entity] || 0;
      });

      return dataPoint;
    });
  }, [distanceByVehicle, filterType, customStart, customEnd, refDate, refMonth]);

  // Obter todas as entidades únicas para a legenda
  const entities = useMemo(() => {
    if (!distanceByVehicle || !Array.isArray(distanceByVehicle) || distanceByVehicle.length === 0) {
      return [];
    }
    const entitySet = new Set<string>();
    distanceByVehicle.forEach(item => entitySet.add(item.entidade));
    return Array.from(entitySet);
  }, [distanceByVehicle]);

  return (
    <Card>
      <CardContent>
        <Box display='flex' justifyContent='space-between' alignItems='center' flexWrap='wrap' style={{ marginBottom: 16 }}>
          <Box display='flex' alignItems='center' style={{ gap: 8 }}>
            <Typography variant='h6' style={{ fontWeight: 'bold' }}>
              DISTÂNCIA PERCORRIDA
            </Typography>
            <ArrowUpward style={{ color: '#4CAF50' }} />
          </Box>

          <Box display='flex' alignItems='center' flexWrap='wrap' style={{ gap: 10 }}>
            <ButtonGroup size="small" aria-label="filtros de período">
              <Button
                color={filterType === 'day' ? 'primary' : 'default'}
                variant={filterType === 'day' ? 'contained' : 'outlined'}
                onClick={() => setFilterType('day')}
              >
                Dia
              </Button>
              <Button
                color={filterType === 'week' ? 'primary' : 'default'}
                variant={filterType === 'week' ? 'contained' : 'outlined'}
                onClick={() => setFilterType('week')}
              >
                Semana
              </Button>
              <Button
                color={filterType === 'fortnight' ? 'primary' : 'default'}
                variant={filterType === 'fortnight' ? 'contained' : 'outlined'}
                onClick={() => setFilterType('fortnight')}
              >
                Quinzena
              </Button>
              <Button
                color={filterType === 'month' ? 'primary' : 'default'}
                variant={filterType === 'month' ? 'contained' : 'outlined'}
                onClick={() => setFilterType('month')}
              >
                Mês
              </Button>
              <Button
                color={filterType === 'custom' ? 'primary' : 'default'}
                variant={filterType === 'custom' ? 'contained' : 'outlined'}
                onClick={() => setFilterType('custom')}
              >
                Entre datas
              </Button>
            </ButtonGroup>
          </Box>
        </Box>

        <Box display='flex' justifyContent='flex-end' alignItems='center' style={{ marginBottom: 16, gap: 16 }}>
          {filterType === 'custom' && (
            <>
              <TextField
                label="Data Inicial"
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
                size="small"
              />
              <TextField
                label="Data Final"
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
                size="small"
              />
            </>
          )}

          {(filterType === 'day' || filterType === 'week' || filterType === 'fortnight') && (
            <TextField
              label={filterType === 'day' ? "Data" : "Data de referência"}
              type="date"
              value={refDate}
              onChange={(e) => setRefDate(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              size="small"
            />
          )}

          {filterType === 'month' && (
            <TextField
              label="Mês de referência"
              type="month"
              value={refMonth}
              onChange={(e) => setRefMonth(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              size="small"
            />
          )}
        </Box>

        {chartData.length === 0 ? (
          <Typography variant='body2' style={{ textAlign: 'center', color: '#666', padding: 40 }}>
            Não há dados disponíveis para o período selecionado
          </Typography>
        ) : (
          <ResponsiveContainer width='100%' height={500}>
            <BarChart
              data={chartData}
              margin={{ top: 20, right: 20, left: 20, bottom: 120 }}
            >
              <CartesianGrid strokeDasharray='3 3' />
              <XAxis
                dataKey='data'
                angle={-45}
                textAnchor='end'
                height={120}
                interval={0}
                tick={{ fontSize: 11 }}
              />
              <YAxis
                label={{ value: 'Distância (Km)', angle: -90, position: 'insideLeft' }}
                domain={[0, 'auto']}
                tick={{ fontSize: 12 }}
              />
              <Tooltip
                formatter={(value: number, name: string) => [
                  `${value} Km`,
                  name,
                ]}
                labelFormatter={(label) => `Data: ${label}`}
                contentStyle={{ fontSize: '12px' }}
              />
              <Legend
                wrapperStyle={{ paddingTop: 10 }}
                iconType='square'
                formatter={(value: string) => {
                  // Truncar nomes longos para caber no layout
                  const maxLength = 20;
                  return value.length > maxLength ? `${value.substring(0, maxLength)}...` : value;
                }}
                style={{ fontSize: '10px' }}
              />
              {entities.map((entity, index) => (
                <Bar
                  key={entity}
                  dataKey={entity}
                  stackId='a'
                  fill={getColorForEntity(entity, index)}
                  name={entity}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}
